import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(article.id, 3);
  const category = categoryMap[article.categoryId];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '久久热',
      url: 'https://mosquitos.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mosquitos.cn/article/${article.id}`,
    },
  };

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://mosquitos.cn',
      },
      ...(category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: category.name,
              item: `https://mosquitos.cn/category/${category.slug}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
            },
          ]
        : [
            {
              '@type': 'ListItem',
              position: 2,
              name: article.title,
            },
          ]),
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
    { label: article.title },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',久久热'}
        canonicalUrl={`https://mosquitos.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaJson={[articleSchema, breadcrumbListSchema]}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${category.icon} text-xs`} />
                  </span>
                  {category.name}
                </Link>
              )}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 mt-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="text-foreground-300">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishedAt}
              </span>
              <span className="text-foreground-300">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 阅读
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        <div className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                prose-p:mb-5 prose-p:leading-relaxed
                prose-strong:text-foreground-900
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-2 h-6 bg-accent-500 rounded-full" />
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  相关推荐
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    id={ra.id}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    coverImage={ra.coverImage}
                    categoryId={ra.categoryId}
                    categoryName={categoryMap[ra.categoryId]?.name || ''}
                    author={ra.author}
                    publishedAt={ra.publishedAt}
                    viewCount={ra.viewCount}
                    isHot={ra.isHot}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}